import axios from "axios";
import type { AxiosRequestConfig, AxiosError } from "axios";
import useLoginStore from "../stores/loginStore";
import { apiRefreshToken } from "./auth/refreshToken";
import { isTokenExpiringSoon } from "../utils/tokenUtils";

// API 응답 타입 정의
export interface IApiResponse<T> {
  success: boolean;
  message: string;
  data: T | null;
  error?: {
    code: string;
    detail: string;
  };
}

// Get API endpoint from environment variables or default
const API_ENDPOINT =
  import.meta.env.VITE_API_ENDPOINT || "https://api.snscbt.com";

// Create axios instance with default configuration
const axiosInstance = axios.create({
  baseURL: API_ENDPOINT,
  headers: {
    "Content-Type": "application/json",
  },
});

// 토큰 리프레시 관련 변수
let isRefreshing = false;
let isPreRefreshing = false; // 사전 리프레시용 플래그
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

// 대기 큐 처리 함수
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Add request interceptor to handle auth tokens
axiosInstance.interceptors.request.use(
  (config) => {
    // 토큰을 스토어에서 가져오기
    const token = useLoginStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      
      // 토큰 만료 3일 전 체크 (로그인, 리프레시 API는 제외)
      if (
        !config.url?.includes("/auth/login") &&
        !config.url?.includes("/auth/refresh") &&
        isTokenExpiringSoon(token, 3) &&
        !isPreRefreshing
      ) {
        isPreRefreshing = true;
        
        // 백그라운드에서 리프레시 시도 (대기하지 않음)
        apiRefreshToken()
          .then(({ token: newToken }) => {
            useLoginStore.getState().setToken(newToken);
            console.log("Token pre-refreshed successfully");
          })
          .catch((error) => {
            // 실패해도 무시 (아직 유효한 토큰이므로)
            console.error("Token pre-refresh failed:", error);
          })
          .finally(() => {
            isPreRefreshing = false;
          });
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle standard API response format
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    // Handle unauthorized errors (e.g., token expired)
    if (error.response?.status === 401 && !originalRequest._retry) {
      // 로그인 페이지나 refresh 엔드포인트에서는 리프레시 시도하지 않음
      if (
        window.location.pathname === "/login" ||
        originalRequest.url?.includes("/auth/refresh") ||
        originalRequest.url?.includes("/auth/login")
      ) {
        useLoginStore.getState().clearToken();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // 이미 리프레시 중이면 대기 큐에 추가
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers!.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { token } = await apiRefreshToken();
        
        // 새 토큰 저장
        useLoginStore.getState().setToken(token);
        
        // 대기 중인 요청들 처리
        processQueue(null, token);
        
        // 원래 요청 재시도
        originalRequest.headers!.Authorization = `Bearer ${token}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // 리프레시 실패 시 처리
        processQueue(refreshError, null);
        
        // 토큰 제거
        useLoginStore.getState().clearToken();

        // 로그인 페이지로 리다이렉트
        window.dispatchEvent(new CustomEvent("auth:unauthorized"));
        setTimeout(() => {
          window.location.href = "/login";
        }, 100);
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // JSON 파싱 오류 또는 응답이 JSON 형식이 아닌 경우에만 처리
    if (
      !error.response ||
      !error.response.data ||
      typeof error.response.data !== "object"
    ) {
      const standardError: IApiResponse<null> = {
        success: false,
        message: "서버와의 통신 중 오류가 발생했습니다.",
        data: null,
        error: {
          code: error.response?.status
            ? `HTTP_${error.response.status}`
            : "NETWORK_ERROR",
          detail: error.message || error.toString(),
        },
      };
      return Promise.reject(standardError);
    }

    // 서버에서 정의된 형식의 오류 응답은 그대로 반환
    return Promise.reject(error.response.data);
  }
);

// 간결한 API 요청 함수들
export const apiGet = async <T>(
  url: string,
  params?: Record<string, any>
): Promise<IApiResponse<T>> => {
  try {
    const response = await axiosInstance.get(url, { params });
    return response.data as IApiResponse<T>;
  } catch (error: any) {
    throw error; // 이미 인터셉터에서 처리된 오류를 그대로 전달
  }
};

export const apiPost = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<IApiResponse<T>> => {
  try {
    const response = await axiosInstance.post(url, data, config);
    return response.data as IApiResponse<T>;
  } catch (error: any) {
    throw error; // 이미 인터셉터에서 처리된 오류를 그대로 전달
  }
};

export const apiPut = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<IApiResponse<T>> => {
  try {
    const response = await axiosInstance.put(url, data, config);
    return response.data as IApiResponse<T>;
  } catch (error: any) {
    throw error; // 이미 인터셉터에서 처리된 오류를 그대로 전달
  }
};

export const apiPatch = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<IApiResponse<T>> => {
  try {
    const response = await axiosInstance.patch(url, data, config);
    return response.data as IApiResponse<T>;
  } catch (error: any) {
    throw error; // 이미 인터셉터에서 처리된 오류를 그대로 전달
  }
};

export const apiDelete = async <T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<IApiResponse<T>> => {
  try {
    const response = await axiosInstance.delete(url, config);
    return response.data as IApiResponse<T>;
  } catch (error: any) {
    throw error; // 이미 인터셉터에서 처리된 오류를 그대로 전달
  }
};

export default axiosInstance;
